import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Edit2, FileText, Info, Save, Sparkles } from 'lucide-react';
import type { ComiteReunionComplete } from '../../services/comiteService';
import { genererResume, saveResume, updateReunion } from '../../services/comiteService';

type ToastFn = (message: string, type: 'success' | 'error') => void;

interface ResumeTabProps {
  reunion: ComiteReunionComplete;
  onUpdate: () => void;
  showToast: ToastFn;
}

export default function ResumeTab({ reunion, onUpdate, showToast }: ResumeTabProps) {
  const [generating, setGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedResume, setEditedResume] = useState('');
  const [verbatim, setVerbatim] = useState(reunion.verbatim || '');
  const [isSavingVerbatim, setIsSavingVerbatim] = useState(false);

  useEffect(() => {
    setVerbatim(reunion.verbatim || '');
  }, [reunion.verbatim]);

  const handleGenerate = async () => {
    setGenerating(true);
    showToast('Génération du résumé en cours...', 'success');
    try {
      if (!reunion.id) throw new Error('ID de réunion manquant');
      const notesText = reunion.notes?.map((n) => `[${n.type}] ${n.contenu}`).join('\n') || 'Aucune note';
      const decisionsText =
        reunion.notes?.filter((n) => n.type === 'decision').map((n) => n.contenu).join('\n') || 'Aucune décision';
      const enjeuxText =
        reunion.notes?.filter((n) => n.type === 'enjeu').map((n) => n.contenu).join('\n') || 'Aucun enjeu';
      const actionsText =
        reunion.actions?.map((a) => `${a.titre} (${a.responsable})`).join('\n') || 'Aucune action';

      await genererResume(reunion.id, notesText, decisionsText, enjeuxText, actionsText);
      showToast('Résumé généré!', 'success');
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la génération', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const startEdit = () => {
    setEditedResume(reunion.resume?.resume_ai || reunion.resume?.resume_manuel || '');
    setIsEditing(true);
  };

  const saveEdit = async () => {
    try {
      if (!reunion.id) return;
      if (!editedResume.trim()) return showToast('Le résumé ne peut pas être vide', 'error');
      await saveResume(reunion.id, editedResume, false);
      showToast('Résumé sauvegardé!', 'success');
      setIsEditing(false);
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la sauvegarde', 'error');
    }
  };

  const saveVerbatim = async () => {
    setIsSavingVerbatim(true);
    try {
      if (!reunion.id) throw new Error('ID de réunion manquant');
      await updateReunion(reunion.id, { verbatim });
      showToast('Verbatim sauvegardé!', 'success');
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la sauvegarde', 'error');
    } finally {
      setIsSavingVerbatim(false);
    }
  };

  const hasNotes = (reunion.notes?.length || 0) > 0;

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          <Sparkles className="w-7 h-7 text-purple-600" />
          Résumé de la Réunion
        </h3>
        <div className="flex items-center gap-2">
          {!isEditing && (
            <button
              onClick={handleGenerate}
              disabled={generating || !hasNotes}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                generating || !hasNotes
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
              }`}
              title={!hasNotes ? 'Ajoutez des notes avant de générer un résumé' : ''}
            >
              <Sparkles className="w-4 h-4" />
              {generating ? 'Génération...' : 'Générer avec IA'}
            </button>
          )}
          {!isEditing && (reunion.resume?.resume_ai || reunion.resume?.resume_manuel) && (
            <button onClick={startEdit} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
              <Edit2 className="w-4 h-4" />
              Modifier
            </button>
          )}
          {isEditing && (
            <>
              <button onClick={saveEdit} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                <Save className="w-4 h-4" />
                Sauvegarder
              </button>
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                Annuler
              </button>
            </>
          )}
        </div>
      </div>

      {(reunion.resume?.resume_ai || reunion.resume?.resume_manuel) ? (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h4 className="font-bold text-purple-900">Résumé</h4>
          </div>
          {isEditing ? (
            <textarea
              value={editedResume}
              onChange={(e) => setEditedResume(e.target.value)}
              rows={14}
              className="w-full px-4 py-3 border-2 border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm bg-white"
            />
          ) : (
            <div className="whitespace-pre-wrap font-sans text-gray-800 bg-white rounded-lg p-5 border border-purple-200 leading-relaxed">
              {reunion.resume?.resume_manuel || reunion.resume?.resume_ai}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-dashed border-purple-300">
          <Sparkles className="w-20 h-20 text-purple-300 mx-auto mb-4" />
          <h4 className="text-xl font-bold text-gray-700 mb-2">Aucun résumé généré</h4>
          <p className="text-gray-600">
            {hasNotes ? 'Cliquez sur “Générer avec IA”' : 'Ajoutez des notes pour pouvoir générer un résumé'}
          </p>
        </div>
      )}

      <div className="bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-gray-300 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-600" />
            <h4 className="font-bold text-gray-900">Verbatim (Transcription Teams)</h4>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Archive de référence
            </span>
          </div>
          <button
            onClick={saveVerbatim}
            disabled={isSavingVerbatim}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              isSavingVerbatim ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-gray-700 text-white hover:bg-gray-800'
            }`}
          >
            <Save className="w-4 h-4" />
            {isSavingVerbatim ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
        <textarea
          value={verbatim}
          onChange={(e) => setVerbatim(e.target.value)}
          rows={8}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 font-mono text-sm bg-white resize-y"
          placeholder="Collez ici la transcription complète de votre réunion Teams..."
        />
        <div className="mt-2 text-xs text-gray-600 flex items-center gap-2">
          <BookOpen className="w-4 h-4" />
          L’IA génère le résumé à partir des notes structurées (pas du verbatim brut).
        </div>
      </div>
    </motion.div>
  );
}

